import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { t } from "i18next";
import {
  ChatEngine,
  MultiConversationEngine,
  ConnectorRegistry,
  MessageTypeRegistry,
  messageStore,
  chatStore,
  createOutgoingMessage,
  applyGlobalSettings,
  type EndOfConversationSurveyConfig,
  type SurveyPayload,
} from "@chativa/core";
import { ChatbotMixin } from "../mixins/ChatbotMixin";
import { registerCommand } from "../commands/index";

import "./DefaultTextMessage";
import "./QuickReplyMessage";
import "./ImageMessage";
import "./CardMessage";
import "./ButtonsMessage";
import "./FileMessage";
import "./VideoMessage";
import "./CarouselMessage";
import "./ChatInput";
import "./ChatMessageList";
import "./ChatHeader";
import "./ConversationList";
import "./EndOfConversationSurvey";
MessageTypeRegistry.setFallback(
  customElements.get("default-text-message") as unknown as typeof HTMLElement
);

const SIZE_PX: Record<string, number> = { small: 44, medium: 56, large: 68 };

@customElement("chat-iva")
export class ChatWidget extends ChatbotMixin(LitElement) {
  static override styles = css`
    :host {
      display: block;
    }

    @keyframes slideUp {
      from { opacity: 0; transform: translateY(18px) scale(0.96); }
      to   { opacity: 1; transform: translateY(0) scale(1); }
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }

    @keyframes slideInFromRight {
      from { opacity: 0; transform: translateX(24px); }
      to   { opacity: 1; transform: translateX(0); }
    }

    @keyframes slideInFromLeft {
      from { opacity: 0; transform: translateX(-24px); }
      to   { opacity: 1; transform: translateX(0); }
    }

    .widget {
      position: fixed;
      display: flex;
      flex-direction: column;
      background: var(--chativa-background, #ffffff);
      border-radius: 16px;
      box-shadow: 0 24px 64px rgba(0, 0, 0, 0.14), 0 6px 20px rgba(0, 0, 0, 0.08);
      overflow: hidden;
      animation: slideUp 0.28s cubic-bezier(0.34, 1.56, 0.64, 1);
      z-index: 9999;
      font-family: var(--chativa-font-family, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif);
      transition: border-radius 0.25s ease, box-shadow 0.25s ease;
    }

    .widget.fullscreen {
      inset: 0 !important;
      width: 100% !important;
      height: 100% !important;
      border-radius: 0 !important;
      animation: fadeIn 0.2s ease;
    }

    /* ── Side-panel mode ───────────────────────────────────── */
    .widget.side-panel {
      border-radius: 0 !important;
      height: 100dvh !important;
      top: 0 !important;
      bottom: 0 !important;
      box-shadow: -4px 0 32px rgba(0, 0, 0, 0.12), -1px 0 8px rgba(0, 0, 0, 0.06);
      animation: slideInFromRight 0.28s cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    .widget.side-panel.from-left {
      box-shadow: 4px 0 32px rgba(0, 0, 0, 0.12), 1px 0 8px rgba(0, 0, 0, 0.06);
      animation: slideInFromLeft 0.28s cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    /* ── Inline mode ───────────────────────────────────────── */
    .widget.inline-mode {
      position: static !important;
      inset: unset !important;
      box-shadow: 0 2px 16px rgba(0, 0, 0, 0.08);
      border-radius: 12px;
      animation: fadeIn 0.15s ease;
      width: 100% !important;
      height: 100% !important;
      min-height: 400px;
    }

    /* ── Conversation list view (popup multi-conv) ─────────── */
    conversation-list {
      flex: 1;
      overflow: hidden;
      animation: slideInFromLeft 0.22s cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    /* Applied directly via JS — no transition during drag */
    .widget.dragging {
      transition: none !important;
      animation: none !important;
      user-select: none;
      cursor: grabbing;
    }

    @media (max-width: 480px) {
      .widget:not(.inline-mode) {
        /*
         * --_vv-top / --_vv-height are set by the visualViewport JS listener
         * so the widget tracks the visible area above the virtual keyboard on iOS.
         * Fallback values (0px / 100dvh) cover Android and non-iOS browsers.
         */
        top: var(--_vv-top, 0px) !important;
        left: 0 !important;
        right: 0 !important;
        bottom: auto !important;
        width: 100% !important;
        height: var(--_vv-height, 100dvh) !important;
        border-radius: 0 !important;
        animation: fadeIn 0.18s ease;
      }
    }

    /* ── File drop overlay ─────────────────────────────────── */
    .drop-overlay {
      position: absolute;
      inset: 0;
      background: rgba(79, 70, 229, 0.07);
      border: 2.5px dashed rgba(79, 70, 229, 0.45);
      border-radius: inherit;
      z-index: 200;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 10px;
      pointer-events: none;
      backdrop-filter: blur(1px);
      animation: fadeIn 0.12s ease;
    }

    .drop-overlay-icon {
      color: var(--chativa-primary-color, #4f46e5);
      opacity: 0.85;
    }

    .drop-overlay-label {
      font-size: 0.9375rem;
      font-weight: 700;
      color: var(--chativa-primary-color, #4f46e5);
      letter-spacing: -0.01em;
    }

    .drop-overlay-sub {
      font-size: 0.8125rem;
      color: rgba(79, 70, 229, 0.65);
    }

  `;

  @property({ type: String }) connector = "dummy";

  /**
   * Set as HTML attribute to start in fullscreen and hide the toggle:
   *   <chat-iva fullscreen-only></chat-iva>
   * Equivalent to: setFullscreen(true) + setAllowFullscreen(false)
   */
  @property({ type: Boolean, attribute: "fullscreen-only" })
  get fulllscreenOnly(): boolean { return false; }
  set fulllscreenOnly(_v: boolean) {
    this.themeState.setFullscreen(true);
    this.themeState.setAllowFullscreen(false);
  }

  // ── File-drop overlay ────────────────────────────────────────────────

  @state() private _showDropOverlay = false;
  @state() private _showConvList = false;
  /** True when screen-mode survey is currently displayed over the message list. */
  @state() private _showSurveyOverlay = false;
  /** True once the end-of-conversation survey has been submitted or skipped this session. */
  private _surveyResolved = false;
  /** True once the inline survey message has been injected this session. */
  private _inlineSurveyInjected = false;
  /** Depth counter to handle dragenter/dragleave firing over child elements. */
  private _dropDepth = 0;

  // ── Drag state — NOT @state to avoid per-frame re-renders ────────────

  /** Last stable drag position (used in _positionStyle). Triggers a render only at drag start/end. */
  private _dragPos: { left: number; top: number } | null = null;
  private _wasOpened = false;

  private _dragAnchor: {
    mouseX: number;
    mouseY: number;
    widgetLeft: number;
    widgetTop: number;
  } | null = null;

  // ── Lifecycle ────────────────────────────────────────────────────────

  private _unsubscribeMessages!: () => void;
  private _engine!: ChatEngine;
  private _multiEngine: MultiConversationEngine | null = null;
  /** True once the engine has been initialised (on first open). */
  private _engineInitialised = false;
  private _vvRafId: number | null = null;

  /**
   * Tracks the visual viewport (the area above the virtual keyboard on iOS).
   * On iOS Safari, `position: fixed` elements shift when the keyboard opens
   * because the visual viewport scrolls. We compensate by setting CSS custom
   * properties `--_vv-top` and `--_vv-height` on the host element; the shadow
   * stylesheet uses `var(--_vv-top)` / `var(--_vv-height)` inside the mobile
   * media query so that the widget always fills exactly the visible area.
   * rAF-throttled to avoid layout thrashing during keyboard animation.
   */
  private _onVisualViewport = () => {
    if (this._vvRafId !== null) return;
    this._vvRafId = requestAnimationFrame(() => {
      this._vvRafId = null;
      if (window.innerWidth > 480) return;
      const vv = window.visualViewport;
      if (!vv) return;
      this.style.setProperty("--_vv-top",    `${Math.round(vv.offsetTop)}px`);
      this.style.setProperty("--_vv-height", `${Math.round(vv.height)}px`);
    });
  };

  connectedCallback() {
    super.connectedCallback();

    // Apply window.chativaSettings (once) before anything else
    applyGlobalSettings();

    // Register built-in slash commands with inline translations
    registerCommand({
      name: "clear",
      translations: {
        en: { description: "Clear all messages from the chat" },
        tr: { description: "Tüm mesajları temizle" },
      },
      execute() {
        messageStore.getState().clear();
      },
    });

    // Prefer connector from global settings, fall back to HTML attribute
    const connectorName = this.themeState.activeConnector !== "dummy"
      ? this.themeState.activeConnector
      : this.connector;
    const adapter = ConnectorRegistry.get(connectorName);
    this._multiEngine = new MultiConversationEngine(adapter);
    this._engine = this._multiEngine.chatEngine;
    this._unsubscribeMessages = messageStore.subscribe(() => this.requestUpdate());
    window.visualViewport?.addEventListener("resize", this._onVisualViewport);
    window.visualViewport?.addEventListener("scroll", this._onVisualViewport);
    document.addEventListener("keydown", this._onKeyDown);
    this.addEventListener("chat-drag-start", this._onDragStart as EventListener);
    this.addEventListener("chat-action", this._onChatAction as EventListener);
    this.addEventListener("chat-retry", this._onChatRetry as EventListener);
    this.addEventListener("chativa-feedback", this._onFeedback as EventListener);
    this.addEventListener("send-file", this._onSendFile as EventListener);
    this.addEventListener("chat-load-history", this._onLoadHistory as EventListener);
    this.addEventListener("genui-send-event", this._onGenUISendEvent as EventListener);
    this.addEventListener("show-conversations", this._onShowConversations as EventListener);
    this.addEventListener("conversation-select", this._onConvSelect as EventListener);
    this.addEventListener("new-conversation", this._onNewConv as EventListener);
    this.addEventListener("conversation-close", this._onConvClose as EventListener);
    this.addEventListener("chat-close-requested", this._onCloseRequested as EventListener);
    this.addEventListener("chat-minimize-requested", this._onMinimizeRequested as EventListener);
    this.addEventListener("chat-reset-survey-state", this._onResetSurveyState as EventListener);
    this.addEventListener("survey-submitted", this._onSurveySubmitted as EventListener);
    this.addEventListener("survey-skipped", this._onSurveySkipped as EventListener);
    this.addEventListener("survey-close", this._onSurveyClose as EventListener);
  }

  disconnectedCallback() {
    this._unsubscribeMessages?.();
    this._multiEngine?.destroy().catch(() => {});
    // connectedCallback builds a fresh engine on re-attach — drop the dead
    // reference and re-arm lazy init so the new engine actually connects.
    this._multiEngine = null;
    this._engineInitialised = false;
    window.visualViewport?.removeEventListener("resize", this._onVisualViewport);
    window.visualViewport?.removeEventListener("scroll", this._onVisualViewport);
    if (this._vvRafId !== null) cancelAnimationFrame(this._vvRafId);
    document.removeEventListener("keydown", this._onKeyDown);
    this.removeEventListener("chat-drag-start", this._onDragStart as EventListener);
    this.removeEventListener("chat-action", this._onChatAction as EventListener);
    this.removeEventListener("chat-retry", this._onChatRetry as EventListener);
    this.removeEventListener("chativa-feedback", this._onFeedback as EventListener);
    this.removeEventListener("send-file", this._onSendFile as EventListener);
    this.removeEventListener("chat-load-history", this._onLoadHistory as EventListener);
    this.removeEventListener("genui-send-event", this._onGenUISendEvent as EventListener);
    this.removeEventListener("show-conversations", this._onShowConversations as EventListener);
    this.removeEventListener("conversation-select", this._onConvSelect as EventListener);
    this.removeEventListener("new-conversation", this._onNewConv as EventListener);
    this.removeEventListener("conversation-close", this._onConvClose as EventListener);
    this.removeEventListener("chat-close-requested", this._onCloseRequested as EventListener);
    this.removeEventListener("chat-minimize-requested", this._onMinimizeRequested as EventListener);
    this.removeEventListener("chat-reset-survey-state", this._onResetSurveyState as EventListener);
    this.removeEventListener("survey-submitted", this._onSurveySubmitted as EventListener);
    this.removeEventListener("survey-skipped", this._onSurveySkipped as EventListener);
    this.removeEventListener("survey-close", this._onSurveyClose as EventListener);
    this._detachDragListeners();
    super.disconnectedCallback();
  }

  // ── Drag handling — direct DOM, zero re-renders during drag ──────────

  private get _windowMode() {
    return this.theme.windowMode ?? "popup";
  }

  private _onDragStart = (e: CustomEvent<{ clientX: number; clientY: number }>) => {
    if (this.themeState.isFullscreen || this._windowMode !== "popup") return;

    const widget = this._widget;
    if (!widget) return;

    const rect = widget.getBoundingClientRect();

    // Store anchor in current left/top coordinates
    this._dragAnchor = {
      mouseX: e.detail.clientX,
      mouseY: e.detail.clientY,
      widgetLeft: rect.left,
      widgetTop: rect.top,
    };

    // Switch from corner-based to left/top positioning (ONE re-render)
    this._dragPos = { left: rect.left, top: rect.top };
    this.requestUpdate();

    // Add dragging class directly — no re-render needed
    this.updateComplete.then(() => {
      this._widget?.classList.add("dragging");
    });

    document.addEventListener("mousemove", this._onMouseMove);
    document.addEventListener("mouseup", this._onMouseUp);
    document.addEventListener("touchmove", this._onTouchMove, { passive: false });
    document.addEventListener("touchend", this._onMouseUp);
  };

  private _onMouseMove = (e: MouseEvent) => this._applyDrag(e.clientX, e.clientY);

  private _onTouchMove = (e: TouchEvent) => {
    e.preventDefault();
    this._applyDrag(e.touches[0].clientX, e.touches[0].clientY);
  };

  private _applyDrag(clientX: number, clientY: number) {
    if (!this._dragAnchor) return;

    const dx = clientX - this._dragAnchor.mouseX;
    const dy = clientY - this._dragAnchor.mouseY;

    const { layout } = this.theme;
    const w = parseInt(layout?.width ?? "360", 10) || 360;
    const h = parseInt(layout?.height ?? "520", 10) || 520;

    const left = Math.min(Math.max(this._dragAnchor.widgetLeft + dx, -w + 80), window.innerWidth - 80);
    const top  = Math.min(Math.max(this._dragAnchor.widgetTop  + dy, 0), window.innerHeight - Math.min(h, 80));

    // Direct DOM update — NO Lit re-render triggered
    const widget = this._widget;
    if (widget) {
      widget.style.left = `${left}px`;
      widget.style.top  = `${top}px`;
    }
    // Keep in sync so _onMouseUp gets the final value cheaply
    this._dragPos = { left, top };
  }

  private _onMouseUp = () => {
    // Remove dragging class directly — no re-render
    this._widget?.classList.remove("dragging");
    this._dragAnchor = null;
    this._detachDragListeners();
    // ONE re-render to bake final position into template style binding
    this.requestUpdate();
  };

  private _detachDragListeners() {
    document.removeEventListener("mousemove", this._onMouseMove);
    document.removeEventListener("mouseup", this._onMouseUp);
    document.removeEventListener("touchmove", this._onTouchMove);
    document.removeEventListener("touchend", this._onMouseUp);
  }

  private get _widget(): HTMLElement | null {
    return this.shadowRoot?.querySelector<HTMLElement>(".widget") ?? null;
  }

  // ── Message sending ──────────────────────────────────────────────────

  private handleSendMessage(e: CustomEvent<string>) {
    const text = e.detail?.trim();
    if (!text) return;
    this._engine
      .send(createOutgoingMessage(text))
      .catch((err: unknown) => console.error("[ChatWidget] Send failed:", err));
  }

  private _onChatAction = (e: Event) => {
    const raw = (e as CustomEvent<string | { text: string; markdown?: boolean }>).detail;
    const text = (typeof raw === "string" ? raw : raw?.text)?.trim();
    const markdown = typeof raw === "object" && raw?.markdown === true;
    if (!text) return;
    const msg = createOutgoingMessage(text);
    if (markdown) msg.data._markdown = true;
    this._engine
      .send(msg)
      .catch((err: unknown) => console.error("[ChatWidget] Action send failed:", err));
  };

  private _onFeedback = (e: CustomEvent<{ messageId: string; feedback: "like" | "dislike" }>) => {
    this._engine
      .sendFeedback(e.detail.messageId, e.detail.feedback)
      .catch((err: unknown) => console.error("[ChatWidget] Feedback failed:", err));
  };

  private _onChatRetry = () => {
    this._engine.init().catch((err: unknown) =>
      console.error("[ChatWidget] Reconnect failed:", err)
    );
  };

  private _onSendFile = (e: CustomEvent<{ files: File[]; text: string }>) => {
    const { files, text } = e.detail;
    for (const file of files) {
      this._engine.sendFile(file, text ? { caption: text } : undefined)
        .catch((err: unknown) => console.error("[ChatWidget] sendFile failed:", err));
    }
  };

  private _onLoadHistory = () => {
    this._engine.loadHistory()
      .catch((err: unknown) => console.error("[ChatWidget] loadHistory failed:", err));
  };

  private _onGenUISendEvent = (e: CustomEvent<{ msgId: string; eventType: string; payload: unknown }>) => {
    this._engine.receiveComponentEvent(e.detail.msgId, e.detail.eventType, e.detail.payload);
  };

  // ── Multi-conversation handlers ───────────────────────────────────────

  private _onShowConversations = () => {
    this._showConvList = true;
  };

  private _onConvSelect = (e: CustomEvent<{ id: string }>) => {
    this._showConvList = false;
    this._multiEngine?.switchTo(e.detail.id).catch((err: unknown) =>
      console.error("[ChatWidget] switchTo failed:", err)
    );
  };

  private _onNewConv = () => {
    this._showConvList = false;
    this._multiEngine?.createNew().catch((err: unknown) =>
      console.error("[ChatWidget] createNew failed:", err)
    );
  };

  private _onConvClose = (e: CustomEvent<{ id: string }>) => {
    this._multiEngine?.close(e.detail.id).catch((err: unknown) =>
      console.error("[ChatWidget] close failed:", err)
    );
  };

  // ── End-of-conversation survey ───────────────────────────────────────

  private _getSurveyConfig(): EndOfConversationSurveyConfig | null {
    const cfg = this.theme.endOfConversationSurvey;
    if (!cfg || !cfg.enabled) return null;
    return cfg;
  }

  /**
   * Decide whether to intercept a user-initiated close in order to show the survey.
   * Called from the header close button and the Escape key path.
   */
  private _requestClose(): void {
    const cfg = this._getSurveyConfig();
    if (!cfg || (cfg.trigger ?? "onClose") !== "onClose") {
      this.themeState.close();
      return;
    }

    // Survey already active this session — either showing the form, showing
    // the thank-you screen, or already submitted. In every case a close
    // press means "wrap up" and should run the reset flow rather than
    // opening another survey.
    if (this._showSurveyOverlay || this._inlineSurveyInjected || this._surveyResolved) {
      this._resetConversation();
      return;
    }

    const mode = cfg.mode ?? "screen";
    if (mode === "screen") {
      this._showSurveyOverlay = true;
      return;
    }

    // inline mode — append a bot-authored survey message and keep the widget open
    this._inlineSurveyInjected = true;
    const Component = MessageTypeRegistry.resolve("end-of-conversation-survey");
    messageStore.getState().addMessage({
      id: `survey-${Date.now()}`,
      type: "end-of-conversation-survey",
      from: "bot",
      data: {},
      timestamp: Date.now(),
      component: Component,
    });
  }

  private _onCloseRequested = () => {
    this._requestClose();
  };

  private _onMinimizeRequested = () => {
    // Minimize bypasses the survey — the user is just hiding the widget.
    this.themeState.close();
  };

  private _onResetSurveyState = () => {
    // Lets tooling (e.g. the sandbox) re-trigger the survey without a full reload.
    this._surveyResolved = false;
    this._inlineSurveyInjected = false;
    this._showSurveyOverlay = false;
  };

  private _onSurveySubmitted = (e: CustomEvent<SurveyPayload & { messageId?: string }>) => {
    const { rating, comment, kind } = e.detail;
    this._engine
      .sendSurvey({ rating, comment, kind })
      .catch((err: unknown) => console.error("[ChatWidget] sendSurvey failed:", err));
    // The component transitions to its thank-you state; reset runs only when
    // the user explicitly clicks the close button (`survey-close`) or when a
    // second close press is interpreted as skip.
    this._surveyResolved = true;
  };

  private _onSurveySkipped = () => {
    this._resetConversation();
  };

  private _onSurveyClose = () => {
    this._resetConversation();
  };

  /**
   * Reset the widget after a survey submit / skip.
   *
   * When `resetOnSubmit` is true (default), fully tear down the engine and
   * connector, wipe runtime state, and rebuild the engine so the next open
   * starts from scratch (fresh connection, empty history, launcher view).
   * Theme config and the active connector *name* are preserved; host pages
   * can listen for `chativa-reset` to swap the registered connector instance
   * before the rebuild (e.g. rotate a DirectLine userId / token).
   *
   * When `resetOnSubmit` is false, the session is kept alive — we only close
   * the widget and reset local survey flags so the next close can trigger
   * the survey again.
   */
  private _resetConversation(): void {
    const cfg = this._getSurveyConfig();
    const doFullReset = cfg?.resetOnSubmit !== false; // default: true

    if (!doFullReset) {
      this._surveyResolved = false;
      this._inlineSurveyInjected = false;
      this._showSurveyOverlay = false;
      this.themeState.close();
      return;
    }

    this.dispatchEvent(
      new CustomEvent("chativa-reset", { bubbles: true, composed: true }),
    );

    const rebuild = () => {
      // Clear runtime state but keep theme, active connector, and fullscreen prefs.
      messageStore.getState().clear();
      chatStore.setState({
        connectorStatus: "idle",
        isTyping: false,
        unreadCount: 0,
        reconnectAttempt: 0,
        hasMoreHistory: false,
        isLoadingHistory: false,
        historyCursor: undefined,
        searchQuery: "",
        activeToolCalls: [],
        isFullscreen: false,
        isRendered: false,
      });

      // Widget-local flags.
      this._surveyResolved = false;
      this._inlineSurveyInjected = false;
      this._showSurveyOverlay = false;
      this._showConvList = false;
      this._showDropOverlay = false;
      this._dropDepth = 0;

      // Rebuild the engine so the next open reconnects fresh. The host page
      // may have swapped the registered connector in its `chativa-reset`
      // listener; ConnectorRegistry.get picks up whatever is there now.
      const connectorName = chatStore.getState().activeConnector;
      const adapter = ConnectorRegistry.get(connectorName);
      this._multiEngine = new MultiConversationEngine(adapter);
      this._engine = this._multiEngine.chatEngine;
      this._engineInitialised = false;

      this.themeState.close();
    };

    const prev = this._multiEngine;
    this._multiEngine = null;
    this._engine = undefined as unknown as ChatEngine;

    if (prev) {
      prev.destroy()
        .catch((err: unknown) => console.error("[ChatWidget] reset: destroy failed:", err))
        .finally(rebuild);
    } else {
      rebuild();
    }
  }

  // ── Widget-level file drop ────────────────────────────────────────────

  private _onFileDragEnter = (e: DragEvent) => {
    if (!e.dataTransfer?.types.includes("Files")) return;
    e.preventDefault();
    this._dropDepth++;
    this._showDropOverlay = true;
  };

  private _onFileDragOver = (e: DragEvent) => {
    if (!e.dataTransfer?.types.includes("Files")) return;
    e.preventDefault();
  };

  private _onFileDragLeave = () => {
    this._dropDepth--;
    if (this._dropDepth <= 0) {
      this._dropDepth = 0;
      this._showDropOverlay = false;
    }
  };

  private _onFileDrop = (e: DragEvent) => {
    e.preventDefault();
    this._dropDepth = 0;
    this._showDropOverlay = false;
    const files = Array.from(e.dataTransfer?.files ?? []);
    if (files.length === 0) return;
    // Forward files to ChatInput queue so the user can optionally add a caption
    const chatInput = this.shadowRoot?.querySelector("chat-input") as (Element & { addFiles(f: File[]): void }) | null;
    chatInput?.addFiles(files);
  };

  // ── Accessibility: focus trap + ESC to close ─────────────────────────

  /** Element focused before the dialog opened — restored when it closes. */
  private _prevActiveElement: HTMLElement | null = null;

  /**
   * Walk the composed shadow tree and collect all focusable elements in DOM
   * order. Needed because querySelectorAll does not pierce shadow roots.
   */
  private _getFocusable(): HTMLElement[] {
    const SELECTOR =
      'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const result: HTMLElement[] = [];
    const collect = (root: ShadowRoot | Element) => {
      for (const el of root.querySelectorAll<HTMLElement>("*")) {
        if ((el as HTMLElement).matches?.(SELECTOR)) result.push(el);
        if (el.shadowRoot) collect(el.shadowRoot);
      }
    };
    if (this.shadowRoot) collect(this.shadowRoot);
    return result;
  }

  private _onKeyDown = (e: KeyboardEvent) => {
    if (!this.themeState.isOpened) return;

    if (e.key === "Escape") {
      e.preventDefault();
      this._requestClose();
      return;
    }

    if (e.key !== "Tab") return;

    const focusable = this._getFocusable();
    if (focusable.length === 0) return;

    // Resolve the deeply-nested active element across shadow roots
    let active: Element | null = document.activeElement;
    while (active?.shadowRoot?.activeElement) {
      active = active.shadowRoot.activeElement;
    }

    const idx = focusable.indexOf(active as HTMLElement);

    if (!e.shiftKey && (idx === -1 || idx >= focusable.length - 1)) {
      e.preventDefault();
      focusable[0].focus();
    } else if (e.shiftKey && idx <= 0) {
      e.preventDefault();
      focusable[focusable.length - 1].focus();
    }
  };

  // ── Focus management ─────────────────────────────────────────────────

  protected override updated(changed: Map<PropertyKey, unknown>) {
    super.updated?.(changed);
    const isOpened = this.themeState.isOpened;

    if (isOpened && !this._wasOpened) {
      // Lazy-connect: initialise the engine on first open.
      // Deferred past this update cycle — init() synchronously sets the
      // connector status in chatStore, which would re-schedule an update
      // while this one is still in progress (Lit change-in-update warning).
      // The isConnected check keeps a deferred init from resurrecting an
      // engine that disconnectedCallback already destroyed.
      if (!this._engineInitialised) {
        this._engineInitialised = true;
        this.updateComplete.then(() => {
          if (!this.isConnected) return;
          this._multiEngine?.init().catch((err: unknown) => {
            console.error("[ChatWidget] Engine init failed:", err);
          });
        });
      }
      // Store previously focused element so we can restore it on close
      this._prevActiveElement = document.activeElement as HTMLElement;
      this.updateComplete.then(() => {
        const input = this.shadowRoot
          ?.querySelector("chat-input")
          ?.shadowRoot?.querySelector<HTMLElement>(".text-input");
        input?.focus();
      });
    }

    if (!isOpened && this._wasOpened) {
      // Return focus to the element that was active before the dialog opened
      this._prevActiveElement?.focus();
      this._prevActiveElement = null;
    }

    this._wasOpened = isOpened;
  }

  // ── Positioning ──────────────────────────────────────────────────────

  private get _positionStyle(): string {
    const mode = this._windowMode;
    const isFullscreen = this.themeState.isFullscreen || mode === "fullscreen";

    if (isFullscreen || mode === "inline") return "";

    const { positionMargin, layout, size, position } = this.theme;
    const w  = layout?.width  ?? "360px";
    const ht = layout?.height ?? "520px";

    if (mode === "side-panel") {
      const side = position.endsWith("left") ? "left: 0" : "right: 0";
      return `${side}; top: 0; width: ${w}; height: 100dvh;`;
    }

    // popup (default)
    if (this._dragPos) {
      return `left: ${this._dragPos.left}px; top: ${this._dragPos.top}px; width: ${w}; height: ${ht};`;
    }

    const hLevel = layout?.horizontalSpace ?? positionMargin ?? "2";
    const vLevel = layout?.verticalSpace   ?? positionMargin ?? "2";
    const mH     = `${Number(hLevel) * 0.5 + 0.5}rem`;
    const mV     = `${Number(vLevel) * 0.5 + 0.5}rem`;
    const btnPx  = SIZE_PX[size ?? "medium"] ?? 56;
    const vOff   = `calc(${mV} + ${btnPx}px + 12px)`;
    const [v, h] = (position ?? "bottom-right").split("-");
    return `${v}: ${vOff}; ${h}: ${mH}; width: ${w}; height: ${ht};`;
  }

  // ── Render ───────────────────────────────────────────────────────────

  render() {
    if (!this.themeState.isOpened) return nothing;

    const mode = this._windowMode;
    const isFullscreen = this.themeState.isFullscreen || mode === "fullscreen";
    const isFromLeft = this.theme.position.endsWith("left");

    const cls = [
      "widget",
      isFullscreen ? "fullscreen" : "",
      mode === "side-panel" ? "side-panel" : "",
      mode === "side-panel" && isFromLeft ? "from-left" : "",
      mode === "inline" ? "inline-mode" : "",
    ].filter(Boolean).join(" ");

    return html`
      <div
        class="${cls}"
        role="dialog"
        aria-modal="true"
        aria-label="${t("widget.title")}"
        style="${this._positionStyle}"
        @dragenter=${this._onFileDragEnter}
        @dragover=${this._onFileDragOver}
        @dragleave=${this._onFileDragLeave}
        @drop=${this._onFileDrop}
      >
        ${this._showConvList && this.theme.enableMultiConversation ? html`
          <conversation-list></conversation-list>
        ` : this._showSurveyOverlay ? html`
          <chat-header .showConvToggle=${this.theme.enableMultiConversation === true}></chat-header>
          <end-of-conversation-survey overlay></end-of-conversation-survey>
        ` : html`
          <chat-header .showConvToggle=${this.theme.enableMultiConversation === true}></chat-header>
          <chat-message-list></chat-message-list>
          <chat-input @send-message=${this.handleSendMessage.bind(this)}></chat-input>
        `}

        ${this._showDropOverlay ? html`
          <div class="drop-overlay" aria-hidden="true">
            <svg class="drop-overlay-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
            </svg>
            <span class="drop-overlay-label">${t("input.dropHere")}</span>
            <span class="drop-overlay-sub">${t("input.dropHereSubtitle")}</span>
          </div>
        ` : nothing}
      </div>
    `;
  }

}

export default ChatWidget;
